import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CertificationsBadges = () => {
  const [selectedCertification, setSelectedCertification] = useState(null);

  const certifications = [
    {
      id: 1,
      name: "AWS Certified Solutions Architect",
      provider: "Amazon Web Services",
      date: "2024-03-15",
      status: "Active",
      credentialId: "AWS-SAA-2024-001",
      logo: "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=100&h=100&fit=crop",
      description: "Validates expertise in designing distributed systems on AWS platform with focus on scalability, security, and cost optimization.",
      skills: ["Cloud Architecture", "AWS Services", "Security", "Cost Optimization"],
      validUntil: "2027-03-15",
      verificationUrl: "https://aws.amazon.com/verification"
    },
    {
      id: 2,
      name: "Google Cloud Professional Developer",
      provider: "Google Cloud Platform",
      date: "2024-01-20",
      status: "Active",
      credentialId: "GCP-PD-2024-002",
      logo: "https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=100&h=100&fit=crop",
      description: "Demonstrates proficiency in developing scalable applications using Google Cloud technologies and best practices.",
      skills: ["GCP Services", "Kubernetes", "Cloud Functions", "BigQuery"],
      validUntil: "2026-01-20",
      verificationUrl: "https://cloud.google.com/certification"
    },
    {
      id: 3,
      name: "Meta React Developer Professional",
      provider: "Meta (Facebook)",
      date: "2023-11-10",
      status: "Active",
      credentialId: "META-RD-2023-003",
      logo: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=100&h=100&fit=crop",
      description: "Comprehensive certification covering advanced React patterns, performance optimization, and modern development practices.",
      skills: ["React 18", "Hooks", "Performance", "Testing"],
      validUntil: "2025-11-10",
      verificationUrl: "https://developers.facebook.com/certification"
    },
    {
      id: 4,
      name: "MongoDB Certified Developer",
      provider: "MongoDB University",
      date: "2023-09-05",
      status: "Active",
      credentialId: "MDB-DEV-2023-004",
      logo: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=100&h=100&fit=crop",
      description: "Validates skills in MongoDB database design, development, and administration for modern applications.",
      skills: ["MongoDB", "Aggregation", "Indexing", "Sharding"],
      validUntil: "2025-09-05",
      verificationUrl: "https://university.mongodb.com/certification"
    },
    {
      id: 5,
      name: "Docker Certified Associate",
      provider: "Docker Inc.",
      date: "2023-07-18",
      status: "Active",
      credentialId: "DCA-2023-005",
      logo: "https://images.unsplash.com/photo-1605745341112-85968b19335b?w=100&h=100&fit=crop",
      description: "Demonstrates expertise in containerization, orchestration, and Docker ecosystem technologies.",
      skills: ["Docker", "Containerization", "Docker Compose", "Swarm"],
      validUntil: "2025-07-18",
      verificationUrl: "https://docker.com/certification"
    },
    {
      id: 6,
      name: "Kubernetes Application Developer",
      provider: "Cloud Native Computing Foundation",
      date: "2023-05-22",
      status: "Active",
      credentialId: "CKAD-2023-006",
      logo: "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=100&h=100&fit=crop",
      description: "Validates skills in designing, building, and deploying cloud-native applications for Kubernetes.",
      skills: ["Kubernetes", "Pods", "Services", "Deployments"],
      validUntil: "2025-05-22",
      verificationUrl: "https://cncf.io/certification"
    }
  ];

  const learningProgress = [
    {
      name: "Advanced TypeScript Patterns",
      provider: "TypeScript Academy",
      progress: 75,
      estimatedCompletion: "2025-02-15",
      description: "Deep dive into advanced TypeScript features and design patterns"
    },
    {
      name: "Microservices Architecture",
      provider: "Cloud Architecture Institute",
      progress: 60,
      estimatedCompletion: "2025-03-20",
      description: "Comprehensive course on designing and implementing microservices"
    },
    {
      name: "Machine Learning for Developers",
      provider: "AI Learning Platform",
      progress: 40,
      estimatedCompletion: "2025-05-10",
      description: "Practical ML implementation for web developers"
    }
  ];

  const getStatusColor = (status) => {
    return status === 'Active' ? 'text-success' : 'text-warning';
  };

  const getStatusBg = (status) => {
    return status === 'Active' ? 'bg-success/10 border-success/20' : 'bg-warning/10 border-warning/20';
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-text-primary mb-4">Certifications & Continuous Learning</h2>
        <p className="text-lg text-text-secondary max-w-3xl mx-auto">
          Professional certifications and ongoing education that validate my expertise and commitment to staying 
          current with industry best practices and emerging technologies.
        </p>
      </div>

      {/* Active Certifications */}
      <div>
        <h3 className="text-2xl font-bold text-text-primary mb-6">Professional Certifications</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {certifications.map((cert) => (
            <div
              key={cert.id}
              className="bg-card rounded-xl border border-border shadow-premium hover:shadow-lg transition-premium hover-lift cursor-pointer"
              onClick={() => setSelectedCertification(cert)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-16 h-16 bg-white rounded-lg shadow-sm flex items-center justify-center overflow-hidden">
                    <Image
                      src={cert.logo}
                      alt={cert.provider}
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBg(cert.status)}`}>
                    <span className={getStatusColor(cert.status)}>{cert.status}</span>
                  </div>
                </div>

                <h4 className="font-bold text-text-primary mb-2 line-clamp-2">{cert.name}</h4>
                <p className="text-sm text-text-secondary mb-3">{cert.provider}</p>
                <p className="text-sm text-text-muted mb-4 line-clamp-2">{cert.description}</p>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Earned:</span>
                    <span className="text-text-primary font-medium">
                      {new Date(cert.date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Valid Until:</span>
                    <span className="text-text-primary font-medium">
                      {new Date(cert.validUntil).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-1">
                  {cert.skills.slice(0, 3).map((skill, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full border border-primary/20"
                    >
                      {skill}
                    </span>
                  ))}
                  {cert.skills.length > 3 && (
                    <span className="text-xs px-2 py-1 bg-surface text-text-secondary rounded-full border border-border">
                      +{cert.skills.length - 3}
                    </span>
                  )}
                </div>

                <button className="w-full mt-4 px-4 py-2 bg-primary/10 text-primary rounded-lg border border-primary/20 hover:bg-primary/20 transition-premium flex items-center justify-center space-x-2">
                  <Icon name="ExternalLink" size={16} />
                  <span>Verify Credential</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Learning in Progress */}
      <div>
        <h3 className="text-2xl font-bold text-text-primary mb-6">Currently Learning</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {learningProgress.map((course, index) => (
            <div
              key={index}
              className="bg-card rounded-xl border border-border shadow-premium p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-accent to-brand-purple rounded-lg flex items-center justify-center">
                  <Icon name="BookOpen" size={24} className="text-white" />
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-text-primary">{course.progress}%</div>
                  <div className="text-xs text-text-secondary">Complete</div>
                </div>
              </div>

              <h4 className="font-bold text-text-primary mb-2">{course.name}</h4>
              <p className="text-sm text-text-secondary mb-3">{course.provider}</p>
              <p className="text-sm text-text-muted mb-4">{course.description}</p>

              <div className="space-y-3">
                <div className="w-full bg-border rounded-full h-2">
                  <div
                    className="h-2 bg-gradient-to-r from-accent to-brand-purple rounded-full transition-all duration-1000"
                    style={{ width: `${course.progress}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-secondary">Est. Completion:</span>
                  <span className="text-text-primary font-medium">
                    {new Date(course.estimatedCompletion).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Certification Detail Modal */}
      {selectedCertification && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-border p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-text-primary">{selectedCertification.name}</h2>
              <button
                onClick={() => setSelectedCertification(null)}
                className="p-2 hover:bg-surface-hover rounded-lg transition-premium"
              >
                <Icon name="X" size={24} className="text-text-secondary" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-white rounded-lg shadow-sm flex items-center justify-center overflow-hidden">
                  <Image
                    src={selectedCertification.logo}
                    alt={selectedCertification.provider}
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-text-primary">{selectedCertification.provider}</h3>
                  <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getStatusBg(selectedCertification.status)}`}>
                    <span className={getStatusColor(selectedCertification.status)}>{selectedCertification.status}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-text-primary mb-2">Description</h4>
                <p className="text-text-secondary">{selectedCertification.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-text-primary mb-3">Certification Details</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Credential ID:</span>
                      <span className="text-text-primary font-mono text-sm">{selectedCertification.credentialId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Earned:</span>
                      <span className="text-text-primary">
                        {new Date(selectedCertification.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Valid Until:</span>
                      <span className="text-text-primary">
                        {new Date(selectedCertification.validUntil).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-text-primary mb-3">Skills Validated</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCertification.skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full border border-primary/20 text-sm"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-premium flex items-center justify-center space-x-2">
                  <Icon name="ExternalLink" size={20} />
                  <span>Verify Credential</span>
                </button>
                <button className="px-6 py-3 bg-surface text-text-primary rounded-lg border border-border hover:bg-surface-hover transition-premium flex items-center justify-center space-x-2">
                  <Icon name="Download" size={20} />
                  <span>Download</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationsBadges;